import React, { useState, useEffect, useCallback, useRef } from 'react';
import './OrderDetails.css';
import { db } from '../../firebase';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy, onSnapshot } from 'firebase/firestore';
import { formatPrice } from '../../utils';
import { useAuth } from '../../hooks/useAuth';
import OrderForm from './OrderForm';
import {
  ORDER_STATUS,
  ORDER_STATUS_LABELS,
  PRODUCT_TYPES,
  PAYMENT_TYPES,
  PAYMENT_TYPE_LABELS
} from '../../constants';

// Merkezi Yardımcı Fonksiyonlar
import {
  getInitialOrderState,
  calculateUsedM2,
  calculateTotalPrice,
  calculateDiscountedPrice,
  calculateCapsulePrice,
  calculateStorZebraUsedM2,
  calculateStorZebraTotalPrice,
  calculateJaluziUsedM2,
  calculateJaluziTotalPrice,
  calculateAksesuarTotalPrice,
  calculateKatlamaliPerdeUsedM,
  calculateKatlamaliPerdeTotalPrice,
  calculateDikeyPerdeTotalPrice,
  calculateTotalCost,
  calculatePileDensity,
  calculatePliseBatchTotals,
  calculateRustikTotalPrice,
  calculateRaySistemiUsedMeter,
  calculateRustikUsedCm,
  calculateAstarPrice
} from './subcomponents/OrderDetailsUtils';

// Alt Bileşenler
import OrderDetailsHeader from './subcomponents/OrderDetailsHeader';
import OrderDetailsList from './subcomponents/OrderDetailsList';
import OrderDetailsSummary from './subcomponents/OrderDetailsSummary';
import OrderDetailsPDF from './subcomponents/OrderDetailsPDF';
import OrderDetailsModals from './subcomponents/OrderDetailsModals';
import OrderDetailsToast from './subcomponents/OrderDetailsToast';

const OrderDetails = ({ customerId, customerName, isEditMode, onCustomerSaved, initialStatus, onOrderStatusChange: onOrderStatusChangeProp, onSaveVadeliSuccess, onVadeliYuzdeChange, onGenelIskontoYuzdeChange }) => {
  const { user, permissions } = useAuth();
  const isMontajci = user?.role === 'montajci';

  const [orders, setOrders] = useState([]);
  const [isAddingOrder, setIsAddingOrder] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [newOrder, setNewOrder] = useState(getInitialOrderState());
  const [isLoading, setIsLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState(initialStatus || ORDER_STATUS.NEW_ORDER);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [paidAmount, setPaidAmount] = useState('0');
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [newPaymentAmount, setNewPaymentAmount] = useState('');
  const [newPaymentType, setNewPaymentType] = useState(PAYMENT_TYPES.CASH);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [genelIskontoYuzde, setGenelIskontoYuzde] = useState('');
  const [vadeliYuzde, setVadeliYuzde] = useState('');
  const [isSavingVadeli, setIsSavingVadeli] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cancelPaymentConfirm, setCancelPaymentConfirm] = useState(null);
  const [showSystemConfirm, setShowSystemConfirm] = useState(false);
  const [pileModels, setPileModels] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const formRef = useRef(null);

  // Toast göster
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  // Fiyat alanlarının ilk yüklemede mi ayarlandığını takip et
  const pricingInitializedRef = useRef(false);

  // Müşteri bilgilerini çek
  useEffect(() => {
    if (!customerId || customerId.startsWith('temp_')) return;
    pricingInitializedRef.current = false; // Yeni müşteri için sıfırla

    const unsubscribe = onSnapshot(doc(db, 'customers', customerId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setCustomerInfo(data);
        setOrderStatus(data.orderStatus || ORDER_STATUS.NEW_ORDER);
        setPaidAmount(String(data.paidAmount || 0));
        setIsPaymentCompleted(data.isPaymentCompleted || false);
        setPaymentHistory(data.paymentHistory || []);
        // Fiyat alanlarını sadece ilk yüklemede ayarla —
        // sonraki snapshot tetiklemeleri kullanıcı girişini silmesin
        if (!pricingInitializedRef.current) {
          setGenelIskontoYuzde(data.genelIskontoYuzde || '');
          setVadeliYuzde(data.vadeliYuzde || '');
          pricingInitializedRef.current = true;
        }
      }
    });

    return () => unsubscribe();
  }, [customerId]);

  // Pile modellerini çek
  useEffect(() => {
    const fetchPileModels = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'pileModels'));
        const models = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPileModels(models);
      } catch (error) {
        console.error('Pile modelleri çekilirken hata:', error);
      }
    };
    fetchPileModels();
  }, []);

  // Siparişleri çek
  const fetchOrders = async () => {
    if (!customerId) return;

    if (customerId.startsWith('temp_')) {
      return;
    }

    try {
      setIsLoading(true);
      const q = query(
        collection(db, 'orders'),
        where('customerId', '==', customerId)
      );
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // İstemci tarafında sıralama - En yeni en üstte
      ordersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setOrders(ordersData);
    } catch (error) {
      console.error('Siparişler çekilirken hata:', error);
      showToast('Siparişler yüklenemedi.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [customerId]);

  // Sipariş durumu değişikliği
  const onOrderStatusChange = async (newStatus) => {
    if (!customerId || customerId.startsWith('temp_')) {
      setOrderStatus(newStatus);
      // Prop varsa bildir (yeni müşteri modunda)
      if (onOrderStatusChangeProp) onOrderStatusChangeProp(newStatus);
      return;
    }

    try {
      setIsLoading(true);
      await updateDoc(doc(db, 'customers', customerId), {
        orderStatus: newStatus,
        updatedAt: new Date()
      });
      setOrderStatus(newStatus);
      // Parent'a (CustomerList) anlık bildir
      if (onOrderStatusChangeProp) onOrderStatusChangeProp(newStatus);
      showToast('Sipariş durumu güncellendi.');
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
      showToast('Durum güncellenirken bir hata oluştu.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // İskonto/Vade Kaydet
  const handleSavePricingFields = async () => {
    if (!customerId || customerId.startsWith('temp_')) return;

    try {
      setIsSavingVadeli(true);
      const isk = parseFloat(genelIskontoYuzde) || 0;
      const vad = parseFloat(vadeliYuzde) || 0;

      // Genel Toplam öncelik zincirine göre hesaplanır (OrderDetails ile aynı mantık)
      const netToplam = vad > 0
        ? totalAmount * (1 + vad / 100)
        : isk > 0
          ? totalAmount * (1 - isk / 100)
          : discountedTotalAmount !== totalAmount
            ? discountedTotalAmount
            : totalAmount;
      const currentRemaining = Math.max(0, netToplam - paidAmountNum);

      await updateDoc(doc(db, 'customers', customerId), {
        genelIskontoYuzde: isk,
        vadeliYuzde: vad,
        remainingAmount: currentRemaining,
        isPaymentCompleted: currentRemaining <= 0.01, // Floating point safety
        updatedAt: new Date()
      });
      const updatedData = {
        id: customerId,
        ...customerInfo,
        genelIskontoYuzde: isk,
        vadeliYuzde: vad,
        remainingAmount: currentRemaining,
        isPaymentCompleted: currentRemaining <= 0.01
      };

      if (onCustomerSaved) onCustomerSaved(updatedData);
      showToast('Fiyat bilgileri kaydedildi.');
    } catch (error) {
      console.error('Fiyat bilgileri kaydedilirken hata:', error);
      showToast('Hata oluştu.', 'error');
    } finally {
      setIsSavingVadeli(false);
    }
  };

  const handleGenelIskontoYuzdeChange = (value) => {
    setGenelIskontoYuzde(value);
    if (onGenelIskontoYuzdeChange) onGenelIskontoYuzdeChange(value);
  };
  const handleVadeliYuzdeChange = (value) => {
    setVadeliYuzde(value);
    if (onVadeliYuzdeChange) onVadeliYuzdeChange(value);
  };

  // Kopyalama sonrası hesaplamaları tetikleyen useEffect
  useEffect(() => {
    if (isAddingOrder && (selectedProductType === 'tul' || selectedProductType === 'kanat') && newOrder.width && newOrder.height && newOrder.pileModel) {
      const updatedOrder = { ...newOrder };
      const density = parseFloat(newOrder.pileDensity) || 0;
      const usedM2 = calculateUsedM2(newOrder.width, newOrder.height, newOrder.pileModel, density > 0 ? density : null);
      updatedOrder.usedM2 = usedM2.toFixed(2);

      if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
        if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.capsuleUnitPrice) {
          updatedOrder.capsulePrice = calculateCapsulePrice(updatedOrder.usedM2 || updatedOrder.width, updatedOrder.capsuleUnitPrice);
        } else if (updatedOrder.pileModel !== 'Kapsül Pile') {
          updatedOrder.capsulePrice = '';
        }

        const mainPricePerUnit = calculateTotalPrice(
          updatedOrder.usedM2,
          updatedOrder.unitPrice,
          updatedOrder.extraPrice,
          updatedOrder.pileModel,
          updatedOrder.capsulePrice
        );
        const quantity = parseFloat(updatedOrder.quantity) || 1;
        const mainTotalPrice = mainPricePerUnit * quantity;

        // Özel tasarım hesaplaması (Sadece Kanat)
        let ozelTotalPrice = 0;
        if (selectedProductType === 'kanat' && updatedOrder.hasOzelTasarim) {
          const ozelDensity = parseFloat(updatedOrder.ozelPileDensity) || 0;
          const ozelUsedM2Val = calculateUsedM2(updatedOrder.ozelWidth, updatedOrder.ozelHeight, updatedOrder.ozelPileModel, ozelDensity > 0 ? ozelDensity : null);
          updatedOrder.ozelUsedM2 = ozelUsedM2Val.toFixed(2);

          if (updatedOrder.ozelPileModel === 'Kapsül Pile' && updatedOrder.ozelCapsuleUnitPrice) {
            updatedOrder.ozelCapsulePrice = calculateCapsulePrice(updatedOrder.ozelUsedM2 || updatedOrder.ozelWidth, updatedOrder.ozelCapsuleUnitPrice);
          } else {
            updatedOrder.ozelCapsulePrice = '';
          }

          const ozelPricePerUnit = calculateTotalPrice(
            updatedOrder.ozelUsedM2,
            updatedOrder.ozelUnitPrice,
            updatedOrder.ozelExtraPrice,
            updatedOrder.ozelPileModel,
            updatedOrder.ozelCapsulePrice
          );
          const ozelQty = parseFloat(updatedOrder.ozelQuantity) || 1;
          ozelTotalPrice = ozelPricePerUnit * ozelQty;
          updatedOrder.ozelTotalPrice = ozelTotalPrice.toFixed(2);
        } else {
          updatedOrder.ozelUsedM2 = '';
          updatedOrder.ozelCapsulePrice = '';
          updatedOrder.ozelTotalPrice = '';
        }

        // Astar hesaplaması (Sadece Kanat)
        let astarTotalPrice = 0;
        if (selectedProductType === 'kanat' && updatedOrder.hasAstar && updatedOrder.astarUnitPrice) {
          const qty = parseFloat(updatedOrder.quantity) || 1;
          astarTotalPrice = calculateAstarPrice(updatedOrder.usedM2, updatedOrder.astarUnitPrice, qty);
          updatedOrder.astarPrice = astarTotalPrice.toFixed(2);
        } else {
          updatedOrder.astarPrice = '';
        }

        // Aksesuarlar (Hem Tül Hem Kanat)
        let accessoriesTotal = 0;
        if (Array.isArray(updatedOrder.accessories)) {
          accessoriesTotal = updatedOrder.accessories.reduce((sum, acc) => {
            const priceVal = parseFloat(acc.price) || 0;
            const qtyVal = parseFloat(acc.quantity) || 0;
            if (acc.unit === 'metre') {
              const metreVal = parseFloat(acc.metre) || 0;
              return sum + (priceVal * qtyVal * metreVal);
            } else {
              return sum + (priceVal * qtyVal);
            }
          }, 0);
        }

        updatedOrder.totalPrice = (mainTotalPrice + ozelTotalPrice + astarTotalPrice + accessoriesTotal).toFixed(2);
      }

      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }
      setNewOrder(updatedOrder);
    }

    if (isAddingOrder && selectedProductType === 'plise') {
      const { items, totalPrice, discountedPrice } = calculatePliseBatchTotals(
        newOrder.pliseItems,
        newOrder.pliseProduct1Price,
        newOrder.pliseProduct2Price,
        newOrder.mounting,
        newOrder.discount,
        newOrder.quantity
      );
      if (totalPrice !== newOrder.totalPrice) {
        setNewOrder(prev => ({ ...prev, pliseItems: items, totalPrice, discountedPrice }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingOrder, selectedProductType]);

  const handleProductTypeChange = (productType) => {
    setSelectedProductType(productType);
    setNewOrder(getInitialOrderState());
  };

  const handleFieldChange = (field, value) => {
    setNewOrder(prevOrder => {
      const updatedOrder = { ...prevOrder, [field]: value };

      if (field === 'motorType' && !value) {
        updatedOrder.motorPrice = 0;
        updatedOrder.motorPriceCurrency = '';
      }

      // Tül ve Kanat
      if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
        if (field === 'pileModel') {
          const density = calculatePileDensity(value);
          updatedOrder.pileDensity = density.toString();
          if (updatedOrder.width && updatedOrder.height) {
            const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, value, density);
            updatedOrder.usedM2 = usedM2.toFixed(2);
          }
        }

        if (field === 'ozelPileModel') {
          const density = calculatePileDensity(value);
          updatedOrder.ozelPileDensity = density.toString();
          if (updatedOrder.ozelWidth && updatedOrder.ozelHeight) {
            const usedM2 = calculateUsedM2(updatedOrder.ozelWidth, updatedOrder.ozelHeight, value, density);
            updatedOrder.ozelUsedM2 = usedM2.toFixed(2);
          }
        }

        if (field === 'hasOzelTasarim' && !value) {
          updatedOrder.ozelProductName = '';
          updatedOrder.ozelProductVariant = '';
          updatedOrder.ozelWidth = '';
          updatedOrder.ozelHeight = '';
          updatedOrder.ozelUnitPrice = '';
          updatedOrder.ozelPileModel = '';
          updatedOrder.ozelPileDensity = '';
          updatedOrder.ozelUsedM2 = '';
          updatedOrder.ozelExtraPrice = '';
          updatedOrder.ozelQuantity = '';
          updatedOrder.ozelBaseUnitPrice = '';
          updatedOrder.ozelCapsuleUnitPrice = '';
          updatedOrder.ozelCapsulePrice = '';
          updatedOrder.ozelTotalPrice = '';
        }

        if (field === 'hasAstar' && !value) {
          updatedOrder.astarProductName = '';
          updatedOrder.astarProductVariant = '';
          updatedOrder.astarUnitPrice = '';
          updatedOrder.astarPrice = '';
        }

        if (['width', 'height', 'unitPrice', 'extraPrice', 'quantity', 'discount', 'pileDensity', 'capsuleUnitPrice', 'pileModel', 'altEtekKatlama',
             'hasOzelTasarim', 'ozelWidth', 'ozelHeight', 'ozelUnitPrice', 'ozelExtraPrice', 'ozelQuantity', 'ozelPileModel', 'ozelPileDensity', 'ozelCapsuleUnitPrice', 'accessories',
             'hasAstar', 'astarUnitPrice'
            ].includes(field)) {
          const density = parseFloat(updatedOrder.pileDensity) || 0;
          const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, density);
          updatedOrder.usedM2 = usedM2.toFixed(2);

          if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.capsuleUnitPrice) {
            updatedOrder.capsulePrice = calculateCapsulePrice(updatedOrder.usedM2 || updatedOrder.width, updatedOrder.capsuleUnitPrice);
          } else if (updatedOrder.pileModel !== 'Kapsül Pile') {
            updatedOrder.capsulePrice = '';
          }

          const mainPricePerUnit = calculateTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.extraPrice,
            updatedOrder.pileModel,
            updatedOrder.capsulePrice
          );
          const qty = parseFloat(updatedOrder.quantity) || 1;
          const mainTotalPrice = mainPricePerUnit * qty;

          // Özel tasarım hesaplaması (Sadece Kanat)
          let ozelTotalPrice = 0;
          if (selectedProductType === 'kanat' && updatedOrder.hasOzelTasarim) {
            const ozelDensity = parseFloat(updatedOrder.ozelPileDensity) || 0;
            const ozelUsedM2Val = calculateUsedM2(updatedOrder.ozelWidth, updatedOrder.ozelHeight, updatedOrder.ozelPileModel, ozelDensity);
            updatedOrder.ozelUsedM2 = ozelUsedM2Val.toFixed(2);

            if (updatedOrder.ozelPileModel === 'Kapsül Pile' && updatedOrder.ozelCapsuleUnitPrice) {
              updatedOrder.ozelCapsulePrice = calculateCapsulePrice(updatedOrder.ozelUsedM2 || updatedOrder.ozelWidth, updatedOrder.ozelCapsuleUnitPrice);
            } else {
              updatedOrder.ozelCapsulePrice = '';
            }

            const ozelPricePerUnit = calculateTotalPrice(
              updatedOrder.ozelUsedM2,
              updatedOrder.ozelUnitPrice,
              updatedOrder.ozelExtraPrice,
              updatedOrder.ozelPileModel,
              updatedOrder.ozelCapsulePrice
            );
            const ozelQty = parseFloat(updatedOrder.ozelQuantity) || 1;
            ozelTotalPrice = ozelPricePerUnit * ozelQty;
            updatedOrder.ozelTotalPrice = ozelTotalPrice.toFixed(2);
          } else {
            updatedOrder.ozelUsedM2 = '';
            updatedOrder.ozelCapsulePrice = '';
            updatedOrder.ozelTotalPrice = '';
          }

          // Astar hesaplaması (Sadece Kanat)
          let astarTotalPrice = 0;
          if (selectedProductType === 'kanat' && updatedOrder.hasAstar && updatedOrder.astarUnitPrice) {
            const astarQty = parseFloat(updatedOrder.quantity) || 1;
            astarTotalPrice = calculateAstarPrice(updatedOrder.usedM2, updatedOrder.astarUnitPrice, astarQty);
            updatedOrder.astarPrice = astarTotalPrice.toFixed(2);
          } else {
            updatedOrder.astarPrice = '';
          }

          // Aksesuarlar (Hem Tül Hem Kanat)
          let accessoriesTotal = 0;
          if (Array.isArray(updatedOrder.accessories)) {
            accessoriesTotal = updatedOrder.accessories.reduce((sum, acc) => {
              const priceVal = parseFloat(acc.price) || 0;
              const qtyVal = parseFloat(acc.quantity) || 0;
              if (acc.unit === 'metre') {
                const metreVal = parseFloat(acc.metre) || 0;
                return sum + (priceVal * qtyVal * metreVal);
              } else {
                return sum + (priceVal * qtyVal);
              }
            }, 0);
          }

          updatedOrder.totalPrice = (mainTotalPrice + ozelTotalPrice + astarTotalPrice + accessoriesTotal).toFixed(2);
          updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
        }
      }

      // Stor ve Zebra
      if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
        if (['width', 'height'].includes(field)) {
          updatedOrder.usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
        }
        const isZebra = selectedProductType === 'zebra';
        const extras = isZebra ? {
          jumbo: updatedOrder.hasZebraJumbo ? updatedOrder.zebraJumboAmount : 0,
          altEtekDilim: updatedOrder.hasZebraAltEtekDilim ? updatedOrder.zebraAltEtekDilimAmount : 0,
          kapaliKasa: updatedOrder.hasZebraKapaliKasa ? updatedOrder.zebraKapaliKasaAmount : 0
        } : {
          jumbo: updatedOrder.hasStorJumbo ? updatedOrder.storJumboAmount : 0,
          altEtekDilim: updatedOrder.hasStorAltEtekDilim ? updatedOrder.storAltEtekDilimAmount : 0,
          kapaliKasa: updatedOrder.hasStorKapaliKasa ? updatedOrder.storKapaliKasaAmount : 0,
          altEtekLazer: updatedOrder.hasStorAltEtekLazer ? updatedOrder.storAltEtekLazerAmount : 0,
          dekoratifLama: updatedOrder.hasStorDekoratifLama ? updatedOrder.storDekoratifLamaAmount : 0,
          dekoratifKasa: updatedOrder.hasStorDekoratifKasa ? updatedOrder.storDekoratifKasaAmount : 0,
          altEtekBeyazIs: updatedOrder.hasStorAltEtekBeyazIs ? updatedOrder.storAltEtekBeyazIsAmount : 0
        };

        const totalPricePreAdet = calculateStorZebraTotalPrice(
          updatedOrder.usedM2, updatedOrder.unitPrice, updatedOrder.width, extras, updatedOrder.motorPrice, updatedOrder.mounting
        );
        updatedOrder.totalPrice = (totalPricePreAdet * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }
      // Jaluzi
      if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') {
        if (['width', 'height'].includes(field)) {
          updatedOrder.usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
        }
        const totalPricePreAdet = calculateJaluziTotalPrice(
          updatedOrder.usedM2, updatedOrder.unitPrice, updatedOrder.mounting,
          updatedOrder.motorPrice, updatedOrder.zigilAmount || updatedOrder.metalZigilAmount || 0,
          updatedOrder.ikiCamArasiAmount || 0
        );
        updatedOrder.totalPrice = (totalPricePreAdet * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Dikey Perde / PVC / Stor — Stor/Zebra ile aynı m² hesabı (en min 1m, boy min 2m)
      if (['dikeyPerde', 'dikeyPvc', 'dikeyStor'].includes(selectedProductType)) {
        if (['width', 'height'].includes(field)) {
          updatedOrder.usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
        }
        // Fon birim fiyata ekleniyorsa (baseUnitPrice varsa) tekrar eklenmesin; eski siparişlerde fallback
        const fonAlreadyInUnitPrice = updatedOrder.baseUnitPrice !== undefined
          && updatedOrder.baseUnitPrice !== null
          && updatedOrder.baseUnitPrice !== '';
        const fonForTotal = fonAlreadyInUnitPrice ? 0 : (updatedOrder.dikeyFonAmount || 0);
        const totalPricePreAdet = calculateDikeyPerdeTotalPrice(
          updatedOrder.usedM2, updatedOrder.unitPrice, fonForTotal,
          updatedOrder.mounting, updatedOrder.motorPrice
        );
        updatedOrder.totalPrice = (totalPricePreAdet * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Katlamalı Perde
      if (selectedProductType === 'katlamaliPerde') {
        if (field === 'width') {
          updatedOrder.usedM = calculateKatlamaliPerdeUsedM(updatedOrder.width).toFixed(2);
        }
        updatedOrder.totalPrice = calculateKatlamaliPerdeTotalPrice(
          updatedOrder.usedM, updatedOrder.fabricPrice, updatedOrder.mechanismPrice,
          updatedOrder.accessories, updatedOrder.mounting, updatedOrder.motorPrice,
          updatedOrder.quantity
        ).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Aksesuar
      if (selectedProductType === 'aksesuar') {
        const uPrice = parseFloat(updatedOrder.unitPrice) || 0;
        const qty = parseFloat(updatedOrder.quantity) || 1;
        const usedM = parseFloat(updatedOrder.usedM) || 1;

        let base = uPrice * qty;
        if (updatedOrder.birimTipi === 'metre') {
          base = uPrice * qty * usedM;
        }

        updatedOrder.totalPrice = (base + (parseFloat(updatedOrder.mounting) || 0)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Ray Sistemi
      if (selectedProductType === 'raySistemi') {
        if (['width', 'olcuTamamlama'].includes(field)) {
          updatedOrder.usedMeter = calculateRaySistemiUsedMeter(updatedOrder.width, updatedOrder.olcuTamamlama).toFixed(2);
        }
        if (updatedOrder.olcuTamamlama) {
          const base = (parseFloat(updatedOrder.usedMeter) || 0) * (parseFloat(updatedOrder.unitPrice) || 0);
          updatedOrder.totalPrice = ((base + (parseFloat(updatedOrder.mounting) || 0)) * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        } else {
          updatedOrder.totalPrice = '0.00';
        }
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Rustik
      if (selectedProductType === 'rustik') {
        const usedCm = calculateRustikUsedCm(updatedOrder.width);
        updatedOrder.usedM2 = usedCm;
        const usedMeters = usedCm / 100;

        if (field === 'width') {
          // Ayak sayısı otomatik seçimi
          if (usedCm <= 200) {
            updatedOrder.ayakAdeti = 2;
          } else if (usedCm < 440) {
            updatedOrder.ayakAdeti = 3;
          } else {
            updatedOrder.ayakAdeti = 4;
          }
        }

        // Ray seçenekleri tutarlarını güncelle
        if (field === 'width' || field === 'rayOptions') {
          if (Array.isArray(updatedOrder.rayOptions)) {
            updatedOrder.rayOptions = updatedOrder.rayOptions.map(opt => ({
              ...opt,
              total: ((parseFloat(opt.price) || 0) * usedMeters).toFixed(2)
            }));
          }
        }
        updatedOrder.totalPrice = calculateRustikTotalPrice(
          updatedOrder.unitPrice,
          updatedOrder.quantity,
          updatedOrder.ayakAdeti,
          updatedOrder.ekAyakSayisi,
          updatedOrder.ayakUcreti,
          updatedOrder.mounting,
          updatedOrder.rayOptions,
          updatedOrder.accessories,
          updatedOrder.usedM2
        ).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Plise
      if (selectedProductType === 'plise') {
        const { items, totalPrice, discountedPrice } = calculatePliseBatchTotals(
          updatedOrder.pliseItems, updatedOrder.pliseProduct1Price, updatedOrder.pliseProduct2Price,
          updatedOrder.mounting, updatedOrder.discount, updatedOrder.quantity
        );
        updatedOrder.pliseItems = items;
        updatedOrder.totalPrice = totalPrice;
        updatedOrder.discountedPrice = discountedPrice;
      }

      // Diğer tipler için genel maliyet hesaplama (unitCost varsa)
      if (['unitCost', 'quantity', 'usedM2'].includes(field)) {
        const tCost = calculateTotalCost(updatedOrder.usedM2, updatedOrder.unitCost);
        updatedOrder.totalCost = (tCost * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
      }

      return updatedOrder;
    });
  };

  const handleAddOrder = async (e, forceSave = false) => {
    if (e) e.preventDefault();
    if (!customerId) return;

    // Stor/Zebra için sistem seçimi kontrolü
    if (!forceSave && (selectedProductType === 'stor' || selectedProductType === 'zebra')) {
      const isSystemSelected = selectedProductType === 'zebra'
        ? (newOrder.hasZebraJumbo || newOrder.hasZebraAltEtekDilim || newOrder.hasZebraKapaliKasa)
        : (newOrder.hasStorJumbo || newOrder.hasStorAltEtekDilim || newOrder.hasStorKapaliKasa ||
          newOrder.hasStorAltEtekLazer || newOrder.hasStorDekoratifLama || newOrder.hasStorDekoratifKasa ||
          newOrder.hasStorAltEtekBeyazIs);

      if (!isSystemSelected) {
        setShowSystemConfirm(true);
        return;
      }
    }

    try {
      const orderData = {
        customerId,
        productType: selectedProductType,
        ...newOrder,
        discount: '',
        discountedPrice: '',
        updatedAt: new Date()
      };

      if (editingOrderId) {
        await updateDoc(doc(db, 'orders', editingOrderId), orderData);
      } else {
        orderData.createdAt = new Date();
        await addDoc(collection(db, 'orders'), orderData);
      }

      // Fiyat alanları doluysa müşteri dokümanına da kaydet
      // Sipariş kaydından bağımsız kendi try-catch bloğunda — hata olursa sipariş yine de kaydedilir
      const isk = parseFloat(genelIskontoYuzde) || 0;
      const vad = parseFloat(vadeliYuzde) || 0;
      if (!customerId.startsWith('temp_') && (isk > 0 || vad > 0)) {
        try {
          // Mevcut orders state'inden + yeni sipariş tutarından toplam hesapla (Firebase read yok)
          const existingOrders = editingOrderId
            ? orders.filter(o => o.id !== editingOrderId)
            : orders;
          const existingTotal = existingOrders.reduce((sum, o) => sum + parseFloat(o.totalPrice || 0), 0);
          const newOrderTotal = parseFloat(newOrder.totalPrice || 0);
          const newTotal = existingTotal + newOrderTotal;

          const netToplam = vad > 0
            ? newTotal * (1 + vad / 100)
            : isk > 0
              ? newTotal * (1 - isk / 100)
              : newTotal;
          const paidNum = parseFloat(paidAmount) || 0;
          const newRemaining = Math.max(0, netToplam - paidNum);

          await updateDoc(doc(db, 'customers', customerId), {
            genelIskontoYuzde: isk,
            vadeliYuzde: vad,
            remainingAmount: newRemaining,
            isPaymentCompleted: newRemaining <= 0.01,
            updatedAt: new Date()
          });
          if (onSaveVadeliSuccess) onSaveVadeliSuccess({ vadeliYuzde, genelIskontoYuzde });
        } catch (pricingError) {
          // Fiyat kaydı başarısız oldu ama sipariş kaydedildi — sessizce logla
          console.warn('Fiyat bilgileri kaydedilemedi (sipariş kaydedildi):', pricingError);
        }
      }

      fetchOrders();
      setIsAddingOrder(false);
      setEditingOrderId(null);
      setNewOrder(getInitialOrderState());
      showToast('Sipariş başarıyla kaydedildi.');
    } catch (error) {
      console.error('Hata:', error);
      showToast('Kaydedilemedi.', 'error');
    }
  };


  const handleEditOrder = (order) => {
    setSelectedProductType(order.productType);
    setNewOrder({
      ...order,
      altEtekKatlama: !!order.altEtekKatlama,
      hasEgimFarki: !!order.hasEgimFarki
    });
    setEditingOrderId(order.id);
    setIsAddingOrder(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCopyOrder = (order) => {
    setSelectedProductType(order.productType);
    const copied = {
      ...order,
      altEtekKatlama: !!order.altEtekKatlama,
      hasEgimFarki: !!order.hasEgimFarki
    };
    delete copied.id;
    delete copied.createdAt;
    setNewOrder(copied);
    setEditingOrderId(null);
    setIsAddingOrder(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDeleteConfirm = (order) => setDeleteConfirm(order);
  const handleDeleteOrder = async () => {
    try {
      await deleteDoc(doc(db, 'orders', deleteConfirm.id));
      setDeleteConfirm(null);
      fetchOrders();
      showToast('Sipariş silindi.');
    } catch (error) {
      showToast('Hata oluştu.', 'error');
    }
  };

  const handleCancelPayment = async (payment) => {
    try {
      const updatedHistory = paymentHistory.filter(p => p.id !== payment.id);
      const totalPaid = updatedHistory.reduce((sum, p) => sum + parseFloat(p.amount), 0);
      const newRemaining = Math.max(0, toplamOdeme - totalPaid);

      await updateDoc(doc(db, 'customers', customerId), {
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      });
      const updatedData = {
        id: customerId,
        ...customerInfo,
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      };

      setCancelPaymentConfirm(null);
      if (onCustomerSaved) onCustomerSaved(updatedData);
      showToast('Ödeme iptal edildi.');
    } catch (error) {
      showToast('İşlem başarısız.', 'error');
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(newPaymentAmount);
    if (!amount) return;

    if (amount + paidAmountNum > toplamOdeme + 0.01) {
      showToast(`Ödeme miktarı kalan tutardan (${formatPrice(remainingAmount)} ₺) fazla olamaz.`, 'error');
      return;
    }

    try {
      setIsAddingPayment(true);
      const newPayment = {
        id: Date.now().toString(),
        amount,
        type: newPaymentType,
        date: new Date().toISOString()
      };
      const updatedHistory = [...paymentHistory, newPayment];
      const totalPaid = updatedHistory.reduce((sum, p) => sum + p.amount, 0);
      const newRemaining = Math.max(0, toplamOdeme - totalPaid);

      await updateDoc(doc(db, 'customers', customerId), {
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      });
      const updatedData = {
        id: customerId,
        ...customerInfo,
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      };

      setNewPaymentAmount('');
      if (onCustomerSaved) onCustomerSaved(updatedData);
      showToast('Ödeme eklendi.');
    } catch (error) {
      showToast('Ödeme eklenemedi.', 'error');
    } finally {
      setIsAddingPayment(false);
    }
  };

  const onToggleAddOrder = () => {
    const nextState = !isAddingOrder;
    setIsAddingOrder(nextState);
    if (nextState) {
      setEditingOrderId(null);
      setSelectedProductType('');
      setNewOrder(getInitialOrderState());
    }
  };

  const handlePaymentCompletedChange = async (checked) => {
    try {
      setIsPaymentCompleted(checked);
      await updateDoc(doc(db, 'customers', customerId), {
        isPaymentCompleted: checked,
        updatedAt: new Date()
      });

      if (onCustomerSaved) {
        onCustomerSaved({
          ...customerInfo,
          id: customerId,
          isPaymentCompleted: checked
        });
      }
      showToast(checked ? 'Ödeme tamamlandı olarak işaretlendi.' : 'Ödeme durumu güncellendi.');
    } catch (error) {
      console.error('Ödeme durumu güncellenirken hata:', error);
      showToast('Hata oluştu.', 'error');
    }
  };

  // Finansal Değişkenler
  const totalAmount = orders.reduce((sum, o) => sum + parseFloat(o.totalPrice || 0), 0);
  const discountedTotalAmount = orders.reduce((sum, o) => sum + parseFloat(o.discountedPrice || o.totalPrice || 0), 0);

  const iskYuzde = parseFloat(genelIskontoYuzde) || 0;
  const vadYuzde = parseFloat(vadeliYuzde) || 0;

  // Her iki oran da doğrudan Ara Toplam üzerinden hesaplanır
  const iskontoluToplam = totalAmount * (1 - iskYuzde / 100); // Ara Toplam - iskonto
  const vadeliToplam = totalAmount * (1 + vadYuzde / 100); // Ara Toplam + vade

  // Genel Toplam (Net) öncelik zinciri:
  // 1. Vade varsa → Vadeli Toplam
  // 2. Sadece iskonto varsa → İskontolu Toplam
  // 3. Sadece ürün indirimi varsa → Ürün İndirimli Toplam
  // 4. Hiçbiri yoksa → Ara Toplam
  const toplamOdeme = vadYuzde > 0
    ? vadeliToplam
    : iskYuzde > 0
      ? iskontoluToplam
      : discountedTotalAmount !== totalAmount
        ? discountedTotalAmount
        : totalAmount;

  const paidAmountNum = parseFloat(paidAmount) || 0;
  const remainingAmount = isPaymentCompleted ? 0 : Math.max(0, toplamOdeme - paidAmountNum);

  return (
    <div className="order-details">
      <OrderDetailsHeader
        customerName={customerName}
        customerInfo={customerInfo}
        orderStatus={orderStatus}
        onOrderStatusChange={onOrderStatusChange}
        isLoading={isLoading}
        isMontajci={isMontajci}
        permissions={permissions}
        isAddingOrder={isAddingOrder}
        onToggleAddOrder={onToggleAddOrder}
      />

      <div ref={formRef}>
        {isAddingOrder && (
          <OrderForm
            isAddingOrder={isAddingOrder}
            editingOrderId={editingOrderId}
            selectedProductType={selectedProductType}
            newOrder={newOrder}
            isLoading={isLoading}
            customerInfo={customerInfo}
            customerId={customerId}
            onProductTypeChange={handleProductTypeChange}
            onFieldChange={handleFieldChange}
            onSubmit={handleAddOrder}
            onCancel={() => setIsAddingOrder(false)}
            pileModels={pileModels}
          />
        )}
      </div>

      <OrderDetailsList
        orders={orders}
        permissions={permissions}
        handleEditOrder={handleEditOrder}
        handleCopyOrder={handleCopyOrder}
        handleDeleteConfirm={handleDeleteConfirm}
        isLoading={isLoading}
      />

      {orders.length > 0 && (
        <>
          <OrderDetailsSummary
            totalAmount={totalAmount}
            discountedTotalAmount={discountedTotalAmount}
            iskontoluToplam={iskontoluToplam}
            vadeliToplam={vadeliToplam}
            toplamOdeme={toplamOdeme}
            remainingAmount={remainingAmount}
            paidAmount={paidAmount}
            isPaymentCompleted={isPaymentCompleted}
            genelIskontoYuzde={genelIskontoYuzde}
            vadeliYuzde={vadeliYuzde}
            handleGenelIskontoYuzdeChange={handleGenelIskontoYuzdeChange}
            handleVadeliYuzdeChange={handleVadeliYuzdeChange}
            handlePaymentCompletedChange={handlePaymentCompletedChange}
            isMontajci={isMontajci}
            isLoading={isLoading}
            isSavingPayment={isSavingPayment}
            isEditMode={isEditMode}
            paymentHistory={paymentHistory}
            setCancelPaymentConfirm={setCancelPaymentConfirm}
            handleSavePricingFields={handleSavePricingFields}
            isSavingVadeli={isSavingVadeli}
            // Payment Form Props
            customerId={customerId}
            orders={orders}
            newPaymentType={newPaymentType}
            setNewPaymentType={setNewPaymentType}
            newPaymentAmount={newPaymentAmount}
            setNewPaymentAmount={setNewPaymentAmount}
            isAddingPayment={isAddingPayment}
            handleAddPayment={handleAddPayment}
          />

          <OrderDetailsPDF
            customerInfo={customerInfo}
            orders={orders}
            totalAmount={totalAmount}
            discountedTotalAmount={discountedTotalAmount}
            genelIskontoYuzde={genelIskontoYuzde}
            vadeliYuzde={vadeliYuzde}
            paidAmount={paidAmount}
            remainingAmount={remainingAmount}
          />
        </>
      )}

      <OrderDetailsModals
        deleteConfirm={deleteConfirm}
        setDeleteConfirm={setDeleteConfirm}
        handleDeleteOrder={handleDeleteOrder}
        cancelPaymentConfirm={cancelPaymentConfirm}
        setCancelPaymentConfirm={setCancelPaymentConfirm}
        handleCancelPayment={handleCancelPayment}
        showSystemConfirm={showSystemConfirm}
        setShowSystemConfirm={setShowSystemConfirm}
        handleAddOrder={handleAddOrder}
      />

      <OrderDetailsToast
        toast={toast}
        setToast={setToast}
      />
    </div>
  );
};

export default OrderDetails;